import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { quoteService } from '../../../services/quoteService';
import { sendQuoteSubmittedEmail } from '../../../services/emailService';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const QuoteSubmissionForm = ({ isOpen, onClose, onSuccess, projectData, vendorData }) => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Check if project has a custom quotesheet
  const hasQuotesheet = projectData?.quoteSettings?.quotesheetActive && projectData?.quoteSettings?.quotesheet;
  
  // Initialize form data based on quotesheet or default structure
  const initializeFormData = () => {
    if (hasQuotesheet) {
      const initialData = {};
      projectData?.quoteSettings?.quotesheet?.sections?.forEach(section => {
        section?.fields?.forEach(field => {
          initialData[field.id] = '';
        });
      });
      return initialData;
    }
    
    // Default form data structure
    return {
      totalCost: '',
      laborCost: '',
      materialCost: '',
      timeline: '',
      timelineUnit: 'weeks',
      startDate: '',
      warranty: '',
      paymentTerms: 'net30',
      proposal: '',
      specialTerms: '',
      contactPreference: 'email',
      availability: 'immediate'
    };
  };

  const [formData, setFormData] = useState(initializeFormData());
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event?.target?.files);
    const newFiles = files?.map(file => ({
      id: Math.random()?.toString(36)?.substr(2, 9),
      name: file?.name,
      size: file?.size,
      type: file?.type
    }));
    setUploadedFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev?.filter(file => file?.id !== fileId));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // ── Quotesheet path ────────────────────────────────────────────────────
      let totalAmount = null;
      let lineItems = {};
      let notes = null;

      if (hasQuotesheet) {
        const quotesheet = projectData?.quoteSettings?.quotesheet;

        // 1. Extract totalAmount from any cost-related field in the quotesheet
        const COST_FIELD_IDS = new Set([
          'price', 'totalCost', 'total_cost', 'totalPrice', 'total_price',
          'cost', 'amount', 'totalAmount', 'total_amount', 'quotedPrice',
          'quoted_price', 'projectCost', 'project_cost', 'totalQuote', 'total_quote'
        ]);
        for (const [fieldId, value] of Object.entries(formData)) {
          if (COST_FIELD_IDS?.has(fieldId) && value) {
            const parsed = parseFloat(String(value)?.replace(/[^0-9.]/g, ''));
            if (!isNaN(parsed) && parsed > 0) {
              totalAmount = parsed;
              break;
            }
          }
        }

        // Fallback: scan all fields for the first numeric value if no standard cost field matched
        if (!totalAmount) {
          for (const [fieldId, value] of Object.entries(formData)) {
            if (value) {
              const parsed = parseFloat(String(value)?.replace(/[^0-9.]/g, ''));
              if (!isNaN(parsed) && parsed > 0) {
                totalAmount = parsed;
                break;
              }
            }
          }
        }

        // 2. Build lineItems: flat { fieldId: value } for scoring engine,
        //    plus _meta with field definitions for category-weighted comparison
        const fieldMeta = {};
        quotesheet?.sections?.forEach(section => {
          section?.fields?.forEach(field => {
            fieldMeta[field?.id] = {
              label: field?.label || field?.id,
              sectionId: section?.id,
              sectionTitle: section?.title,
              type: field?.type,
              required: field?.required || false,
              priority: field?.priority || null,
            };
          });
        });

        // Flat responses (fieldId → value string) — used by scoring engine
        const responses = {};
        Object.entries(formData)?.forEach(([fieldId, value]) => {
          if (value !== '' && value != null) {
            responses[fieldId] = value;
          }
        });

        lineItems = {
          ...responses,
          _meta: fieldMeta,
          _quotesheetId: quotesheet?.id || null,
          _submittedAt: new Date()?.toISOString(),
        };

        // 3. Build human-readable notes grouped by section
        const sectionSummaries = quotesheet?.sections
          ?.map(section => {
            const fieldLines = section?.fields
              ?.filter(field => formData?.[field?.id] !== '' && formData?.[field?.id] != null)
              ?.map(field => `  • ${field?.label}: ${formData?.[field?.id]}`)
              ?.join('\n');
            if (!fieldLines) return null;
            return `${section?.title}\n${fieldLines}`;
          })
          ?.filter(Boolean);

        notes = sectionSummaries?.length > 0
          ? sectionSummaries?.join('\n\n')
          : null;

      } else {
        // ── Default (non-quotesheet) path ──────────────────────────────────
        totalAmount = formData?.totalCost ? parseFloat(formData?.totalCost) : null;

        lineItems = {
          deliveryTime: formData?.timeline ? `${formData?.timeline} ${formData?.timelineUnit}` : null,
          specifications: formData?.proposal || null,
          specs: [
            formData?.laborCost ? `Labor: $${parseFloat(formData?.laborCost)?.toLocaleString()}` : null,
            formData?.materialCost ? `Materials: $${parseFloat(formData?.materialCost)?.toLocaleString()}` : null,
            formData?.warranty ? `Warranty: ${formData?.warranty}` : null,
            formData?.paymentTerms ? `Payment Terms: ${formData?.paymentTerms}` : null,
            formData?.availability ? `Availability: ${formData?.availability}` : null,
          ]?.filter(Boolean)?.join(', ') || null,
          productUrl: null,
        };

        notes = [
          formData?.proposal,
          formData?.specialTerms ? `Special Terms: ${formData?.specialTerms}` : null,
          formData?.warranty ? `Warranty: ${formData?.warranty}` : null,
          formData?.timeline ? `Timeline: ${formData?.timeline} ${formData?.timelineUnit}` : null,
        ]?.filter(Boolean)?.join('\n\n') || null;
      }

      // ── Persist to Supabase ────────────────────────────────────────────────
      const existingQuoteId = projectData?.existingQuoteId;
      if (existingQuoteId) {
        await quoteService?.update(existingQuoteId, {
          status: 'submitted',
          totalAmount,
          lineItems,
          notes,
          submittedAt: new Date()?.toISOString(),
        });
      } else {
        await quoteService?.submitQuote({
          projectId: projectData?.id,
          vendorId: vendorData?.vendorId || null,
          vendorName: vendorData?.businessName,
          totalAmount,
          lineItems,
          notes,
          validUntil: null,
          projectOwnerId: projectData?.ownerId || null,
          projectOwnerEmail: projectData?.clientEmail || null,
          projectName: projectData?.title || projectData?.name || null,
        });
      }

      // ── Email notification ─────────────────────────────────────────────────
      if (projectData?.clientEmail) {
        await sendQuoteSubmittedEmail(projectData?.clientEmail, {
          projectName: projectData?.title || projectData?.name,
          vendorName: vendorData?.businessName,
          totalAmount,
          projectOwnerName: projectData?.clientName,
          projectUrl: `${window.location?.origin}/project-comparison`,
        });
      }

      onSuccess({
        vendorName: vendorData?.businessName,
        projectName: projectData?.title || projectData?.name,
        totalAmount,
        timeline: hasQuotesheet
          ? (formData?.timeline || formData?.completionTimeline || null)
          : (formData?.timeline ? `${formData?.timeline} ${formData?.timelineUnit}` : null),
        proposal: hasQuotesheet ? null : (formData?.proposal || null),
        warranty: hasQuotesheet
          ? (formData?.warranty || formData?.warrantyPeriod || null)
          : (formData?.warranty || null),
        submittedAt: new Date()?.toISOString(),
      });
    } catch (error) {
      console.error('Quote submission failed:', error);
      setSubmitError(error?.message || 'Failed to submit quote. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderQuotesheetForm = () => {
    if (!hasQuotesheet) return null;

    const quotesheet = projectData?.quoteSettings?.quotesheet;

    return (
      <div className="space-y-8">
        {/* Quotesheet Header */}
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <Icon name="FileText" size={20} className="mr-2 text-primary" />
            <h3 className="font-semibold text-foreground">Quote Request Questionnaire</h3>
          </div>
          <p className="text-sm text-text-secondary">
            Please answer all questions marked with an asterisk (*). Your responses will be used for comparative analysis.
          </p>
          {quotesheet?.customInstructions && (
            <div className="mt-3 p-3 bg-background rounded border border-border">
              <p className="text-sm text-foreground">
                <strong>Special Instructions:</strong> {quotesheet?.customInstructions}
              </p>
            </div>
          )}
        </div>
        {/* Render Dynamic Sections */}
        {quotesheet?.sections?.map((section) => (
          <div key={section?.id} className="space-y-4">
            <div className="border-l-4 border-primary pl-4 mb-4">
              <div className="flex items-center space-x-2">
                <Icon name="HelpCircle" size={20} className="text-primary" />
                <h3 className="text-lg font-semibold text-foreground">{section?.title}</h3>
              </div>
              {section?.description && (
                <p className="text-sm text-text-secondary mt-1">{section?.description}</p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-6">
              {section?.fields?.map((field, index) => (
                <div key={field?.id} className="bg-surface/50 border border-border rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center mt-1">
                      <span className="text-sm font-semibold text-primary">{index + 1}</span>
                    </div>
                    <div className="flex-1">
                      <label className="block text-base font-medium text-foreground mb-2">
                        {field?.label}
                      </label>
                      {field?.description && (
                        <p className="text-xs text-text-secondary mb-3 italic">{field?.description}</p>
                      )}
                      {field?.priority && (
                        <span className={`inline-block text-xs px-2 py-1 rounded mb-3 ${
                          field?.priority === 'High' ? 'bg-error/10 text-error' :
                          field?.priority === 'Medium'? 'bg-warning/10 text-warning' : 'bg-success/10 text-success'
                        }`}>
                          {field?.priority} Priority
                        </span>
                      )}
                      
                      {field?.type === 'text' || field?.type === 'email' || field?.type === 'tel' || field?.type === 'url' || field?.type === 'number' || field?.type === 'date' ? (
                        <Input
                          type={field?.type}
                          placeholder={field?.required ? 'Required' : 'Optional'}
                          value={formData?.[field?.id] || ''}
                          onChange={(e) => handleInputChange(field?.id, e?.target?.value)}
                          required={field?.required}
                          className="w-full"
                        />
                      ) : field?.type === 'textarea' ? (
                        <textarea
                          placeholder={field?.required ? 'Required - Please provide detailed response' : 'Optional - Please provide your response'}
                          value={formData?.[field?.id] || ''}
                          onChange={(e) => handleInputChange(field?.id, e?.target?.value)}
                          required={field?.required}
                          rows={4}
                          className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                        />
                      ) : field?.type === 'select' && field?.options ? (
                        <Select
                          options={[{ value: '', label: 'Select your answer' }, ...field?.options]}
                          value={formData?.[field?.id] || ''}
                          onChange={(value) => handleInputChange(field?.id, value)}
                          required={field?.required}
                        />
                      ) : field?.type === 'file' ? (
                        <div>
                          <input
                            type="file"
                            accept={field?.accept}
                            onChange={handleFileUpload}
                            multiple
                            className="w-full text-sm text-text-secondary file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary/90"
                          />
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {/* Uploaded Files Display */}
        {uploadedFiles?.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium text-foreground">Uploaded Files</h4>
            <div className="space-y-2">
              {uploadedFiles?.map(file => (
                <div key={file?.id} className="flex items-center justify-between p-3 bg-surface rounded-lg border border-border">
                  <div className="flex items-center space-x-3">
                    <Icon name="FileText" size={18} className="text-primary" />
                    <div>
                      <p className="text-sm font-medium text-foreground">{file?.name}</p>
                      <p className="text-xs text-text-secondary">{formatFileSize(file?.size)}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeFile(file?.id)}
                  >
                    <Icon name="X" size={16} />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDefaultForm = () => {
    return (
      <div className="space-y-8">
        {/* Pricing Section */}
        <div className="space-y-6">
          <div className="flex items-center space-x-2 mb-4">
            <Icon name="DollarSign" size={20} className="text-success" />
            <h3 className="text-lg font-semibold text-foreground">Pricing Details</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Total Project Cost *
              </label>
              <Input
                type="number"
                placeholder="Enter total cost"
                value={formData?.totalCost}
                onChange={(e) => handleInputChange('totalCost', e?.target?.value)}
                required
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Labor Cost
              </label>
              <Input
                type="number"
                placeholder="Labor breakdown"
                value={formData?.laborCost}
                onChange={(e) => handleInputChange('laborCost', e?.target?.value)}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Material Cost
              </label>
              <Input
                type="number"
                placeholder="Materials breakdown"
                value={formData?.materialCost}
                onChange={(e) => handleInputChange('materialCost', e?.target?.value)}
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* Timeline Section */}
        <div className="space-y-4">
          <div className="flex items-center space-x-2 mb-4">
            <Icon name="Clock" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Timeline & Availability</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Estimated Duration *
              </label>
              <Input
                type="number"
                placeholder="Duration"
                value={formData?.timeline}
                onChange={(e) => handleInputChange('timeline', e?.target?.value)}
                required
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Unit
              </label>
              <Select
                options={[
                  { value: 'days', label: 'Days' },
                  { value: 'weeks', label: 'Weeks' },
                  { value: 'months', label: 'Months' }
                ]}
                value={formData?.timelineUnit}
                onChange={(value) => handleInputChange('timelineUnit', value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Availability
              </label>
              <Select
                options={[
                  { value: 'immediate', label: 'Immediate' },
                  { value: '1-2weeks', label: '1-2 Weeks' },
                  { value: '1month', label: '1 Month' },
                  { value: 'flexible', label: 'Flexible' }
                ]}
                value={formData?.availability}
                onChange={(value) => handleInputChange('availability', value)}
              />
            </div>
          </div>
        </div>

        {/* Proposal Section */}
        <div className="space-y-4">
          <div className="flex items-center space-x-2 mb-4">
            <Icon name="FileText" size={20} className="text-warning" />
            <h3 className="text-lg font-semibold text-foreground">Proposal Details</h3>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Project Proposal *
            </label>
            <textarea
              placeholder="Describe your approach, methodology, and what sets you apart..."
              value={formData?.proposal}
              onChange={(e) => handleInputChange('proposal', e?.target?.value)}
              required
              rows={5}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Warranty / Guarantee
            </label>
            <Input
              type="text"
              placeholder="e.g., 2-year workmanship warranty"
              value={formData?.warranty}
              onChange={(e) => handleInputChange('warranty', e?.target?.value)}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Special Terms or Conditions
            </label>
            <textarea
              placeholder="Any special terms, payment conditions, or requirements..."
              value={formData?.specialTerms}
              onChange={(e) => handleInputChange('specialTerms', e?.target?.value)}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
            />
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  // ── Auth gate: block unauthenticated vendors ──────────────────────────────
  if (!isAuthenticated) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 py-8">
          <div className="fixed inset-0 bg-black/50" onClick={onClose} />
          <div className="relative z-10 bg-background rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-br from-primary/10 via-background to-primary/5 px-6 py-8 text-center border-b border-border">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Lock" size={32} className="text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">Registration Required</h2>
              <p className="text-text-secondary text-sm">
                You must be registered and logged in as a vendor to submit a quote.
              </p>
            </div>

            {/* Body */}
            <div className="px-6 py-6 space-y-4">
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 text-sm text-text-secondary leading-relaxed">
                <div className="flex items-start space-x-3">
                  <Icon name="Info" size={16} className="mt-0.5 text-primary flex-shrink-0" />
                  <span>
                    Creating a free vendor account lets you submit quotes, track your bids, and communicate directly with clients on the Stackd platform.
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <Button
                  variant="default"
                  className="w-full bg-primary hover:bg-primary/90"
                  onClick={() => {
                    onClose();
                    navigate('/login-sign-up?mode=signup&role=vendor');
                  }}
                  iconName="UserPlus"
                  iconPosition="left"
                >
                  Create Vendor Account
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    onClose();
                    navigate('/login-sign-up?mode=signin');
                  }}
                  iconName="LogIn"
                  iconPosition="left"
                >
                  Sign In to Existing Account
                </Button>
                <button
                  onClick={onClose}
                  className="w-full text-sm text-text-secondary hover:text-foreground transition-colors py-2"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Render form (authenticated vendors only) ──────────────────────────────
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-start justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />
        
        <div className="inline-block align-bottom bg-background rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full relative z-10">
          {/* Modal Header */}
          <div className="bg-gradient-to-r from-primary/10 to-success/10 px-6 py-5 border-b border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                  <Icon name="FileText" size={20} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-foreground">Submit Your Quote</h2>
                  <p className="text-sm text-text-secondary">
                    {vendorData?.businessName} → {projectData?.title || projectData?.name}
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <Icon name="X" size={20} />
              </Button>
            </div>
          </div>

          {/* Modal Body */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-6 max-h-[70vh] overflow-y-auto">
              {hasQuotesheet ? renderQuotesheetForm() : renderDefaultForm()}

              {submitError && (
                <div className="mt-4 p-3 bg-error/10 border border-error/20 rounded-lg flex items-center space-x-2">
                  <Icon name="AlertCircle" size={16} className="text-error flex-shrink-0" />
                  <p className="text-sm text-error">{submitError}</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-surface/50 border-t border-border flex items-center justify-between">
              <Button variant="outline" type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="default"
                type="submit"
                disabled={isSubmitting}
                iconName={isSubmitting ? undefined : "Send"}
                iconPosition="right"
                className="bg-primary hover:bg-primary/90"
              >
                {isSubmitting ? (
                  <span className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    <span>Submitting...</span>
                  </span>
                ) : 'Submit Quote'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default QuoteSubmissionForm;